/**
 * formSnapshot.ts — Filet de sécurité générique pour TOUS les formulaires admin.
 *
 * Principe : juste avant une déconnexion forcée (401 irrécupérable), on capture
 * la valeur de CHAQUE champ visible de la page (input/select/textarea, y compris
 * dans un modal ouvert). Après re-connexion + redirection vers la page d'origine,
 * les champs sont re-peuplés automatiquement — sans rien modifier dans les pages.
 *
 * S'intègre en 2 endroits :
 *   1. api.ts  → captureFormSnapshot() dans la branche d'échec du refresh (401)
 *   2. LoginPage → restoreFormSnapshot(target) juste après navigate(target)
 *      (ou useFormSnapshotRestore() dans le layout admin si React Router)
 *
 * Limite honnête : les valeurs sont restaurées, mais un modal fermé par la
 * déconnexion devra être rouvert manuellement (les valeurs ré-apparaîtront).
 */
const KEY = (path: string) => `form_snapshot:${path}`

/** Capture tous les champs visibles de la page courante. */
export function captureFormSnapshot(): void {
  try {
    const data: Record<string, { t: 'value' | 'checked'; v: any }> = {}
    document.querySelectorAll<HTMLElement>('input, select, textarea').forEach(el => {
      const name = el.getAttribute('name') || el.id
      if (!name) return
      if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
        if (el.checked) data[name] = { t: 'checked', v: true }
      } else if (el instanceof HTMLInputElement && el.type === 'file') {
        // fichiers non sérialisables → ignorés volontairement
      } else {
        const val = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value
        if (val) data[name] = { t: 'value', v: val }
      }
    })
    sessionStorage.setItem(
      KEY(window.location.pathname + window.location.search),
      JSON.stringify(data)
    )
  } catch { /* sessionStorage indisponible */ }
}

/**
 * Restaure les champs de la page. Retourne true si un snapshot a été appliqué.
 * Utilise le "native setter trick" pour que React enregistre les valeurs
 * sur ses inputs contrôlés.
 */
export function restoreFormSnapshot(path?: string): boolean {
  try {
    const key = KEY(path ?? window.location.pathname + window.location.search)
    const raw = sessionStorage.getItem(key)
    if (!raw) return false
    sessionStorage.removeItem(key)
    const data = JSON.parse(raw)

    // Double rAF : attendre que la page (et ses listes/modals) soient rendues
    requestAnimationFrame(() => requestAnimationFrame(() => {
      Object.entries<any>(data).forEach(([name, field]) => {
        const el = document.querySelector<HTMLElement>(
          `[name="${CSS.escape(name)}"], #${CSS.escape(name)}`
        )
        if (!el) return
        if (field.t === 'checked' && el instanceof HTMLInputElement) {
          el.checked = true
          el.dispatchEvent(new Event('click', { bubbles: true }))
        } else {
          const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype
                      : el instanceof HTMLSelectElement ? HTMLSelectElement.prototype
                      : HTMLInputElement.prototype
          const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
          if (!setter) return
          setter.call(el, field.v)
          el.dispatchEvent(new Event('input', { bubbles: true }))
          el.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
    }))
    return true
  } catch { return false }
}

/** Variante React Router : restaure automatiquement à chaque changement de route. */
// import { useLocation } from 'react-router-dom'
// export function useFormSnapshotRestore() {
//   const location = useLocation()
//   useEffect(() => { restoreFormSnapshot() }, [location.pathname, location.search])
// }
